import { Box } from "@mui/material";
import PageviewIcon from "@mui/icons-material/PageviewOutlined";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ActionBanner } from "../../../common/components/functional/actionBanner/ActionBanner";
import {
  ActionButton,
  ActionButtonGroup,
  GroupButton,
} from "../../../common/components/functional/actionBanner/styled";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useMessage from "../../../hooks/useMessage";
import {
  canExpertExamineInvoice,
  canExpertModifyInvoices,
} from "../../user/accessRightChecks";
import { InvoiceActionBannerPagingControl } from "../common/InvoiceActionBannerPagingControl";
import {
  INVOICE_DRAFT,
  INVOICE_EDITED,
  INVOICE_READY,
  INVOICE_SENT_FAILED,
  INVOICE_SENT_SUCCESS,
  INVOICE_WAVE_ONE,
  INVOICE_WAVE_TWO,
} from "../constants";
import { InvoiceEdit, InvoiceRow, InvoiceWavePost } from "../invoiceAPI";
import { copyInvoice } from "../invoiceDetailedSlice";
import { changeInvoiceWave, sendInvoices } from "../invoiceSlice";
import { isInvoiceCreditInvoice } from "../utils";
import { ActionToggleButton } from "../../../common/components/styled/styled";

interface Props {
  translation: string;
  selectedInvoice: InvoiceEdit;
  currentInvoice: number;
  numberOfInvoices: number;
  cycleInvoices: (increment: number) => void;
  creditInvoiceBundleInvoices: InvoiceEdit[];
  toggleCreateNewCreditInvoiceDialog: () => void;
  toggleConvertToCreditInvoiceDialog: () => void;
  isRowSelectionEnabled: boolean;
  toggleRowSelectionEnabled: () => void;
}

export const PreviewInvoiceActionBanner = ({
  translation,
  selectedInvoice,
  currentInvoice,
  numberOfInvoices,
  cycleInvoices,
  creditInvoiceBundleInvoices,
  toggleCreateNewCreditInvoiceDialog,
  toggleConvertToCreditInvoiceDialog,
  isRowSelectionEnabled,
  toggleRowSelectionEnabled,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sendMessage } = useMessage();
  const { t } = useTranslation(translation);

  const { status } = useAppSelector((state) => state.invoicesDetailed);
  const isLoading = status === "pending";
  const user = useAppSelector((state) => state.user.user);
  const canExamine = !!user && canExpertExamineInvoice(user);

  const [isUserAllowedToEdit, setIsUserAllowedToEdit] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [confirmationContent, setConfirmationContent] = useState({
    title: "",
    contentText: "",
    cancelText: "",
    confirmText: "",
    confirmAction: () => {},
  });

  // Note: edit view is always allowed in vaihe 1 for viewing attachments (with edit
  // functionalities disabled after invoice is sent)
  // It should be disabled again after a proper way for the viewing is implemented
  const [canEdit] = useState<boolean>(true);
  const [canMarkFirstWave, setCanMarkFirstWave] = useState<boolean>(true);
  const [canMarkSecondWave, setCanMarkSecondWave] = useState<boolean>(true);
  const [canSend, setCanSend] = useState<boolean>(true);
  const [canResend, setCanResend] = useState<boolean>(true);
  const [
    areCreditInvoiceBundleStatusesValidForSameAction,
    setAreCreditInvoiceBundleStatusesValidForSameAction,
  ] = useState<boolean>(true);

  const isSelectedCreditInvoiceBundleItem =
    creditInvoiceBundleInvoices.length > 0 &&
    creditInvoiceBundleInvoices.find(
      (creditInvoice) =>
        creditInvoice.invoiceNumber === selectedInvoice.invoiceNumber!!,
    );

  // If multiple credit invoice bundles are opened for Preview, need to know which bundle items we are handling
  const selectedCreditInvoiceBundleItems = useMemo(
    () =>
      isSelectedCreditInvoiceBundleItem
        ? creditInvoiceBundleInvoices.filter(
            (creditInvoice) =>
              creditInvoice.originalInvoiceNumber ===
              selectedInvoice.originalInvoiceNumber!!,
          )!!
        : [],
    [
      isSelectedCreditInvoiceBundleItem,
      creditInvoiceBundleInvoices,
      selectedInvoice.originalInvoiceNumber,
    ],
  );

  const isValidCreditInvoiceBundle =
    isSelectedCreditInvoiceBundleItem &&
    areCreditInvoiceBundleStatusesValidForSameAction;

  const markWave = (wave: string) => {
    const invoiceWaveInfo: InvoiceWavePost = {
      version: selectedInvoice.version!!,
      invoiceId: selectedInvoice.id!!,
      invoiceSentWave: wave,
    };
    dispatch(changeInvoiceWave(invoiceWaveInfo)).then(
      (action) =>
        isFulfilled(action) && sendMessage(t("waveUpdated"), "success"),
    );
  };

  const send = () => {
    // Credit invoice bundle case
    if (isValidCreditInvoiceBundle) {
      const creditInvoiceBundleInvoiceNumbers =
        selectedCreditInvoiceBundleItems.map(
          (creditInvoice) => creditInvoice.id!!,
        );
      dispatch(sendInvoices(creditInvoiceBundleInvoiceNumbers));
    } else {
      // Normal case
      dispatch(sendInvoices([selectedInvoice.id!!]));
    }
  };

  const copy = () => {
    dispatch(copyInvoice(selectedInvoice)).then(
      (action) => isFulfilled(action) && navigate("/home/invoices/new"),
    );
  };

  const resetConfirmation = () => {
    setIsConfirmationDialogOpen(false);
    setConfirmationContent({
      title: "",
      contentText: "",
      cancelText: "",
      confirmText: "",
      confirmAction: () => {},
    });
  };

  const confirmSending = () => {
    setConfirmationContent({
      title: t("confirmSending"),
      contentText: isValidCreditInvoiceBundle
        ? t("confirmSendingCreditInvoice")
        : t("confirmSendingInfo"),
      cancelText: t("common:cancel"),
      confirmText: t("send"),
      confirmAction: () => {
        resetConfirmation();
        send();
      },
    });
    setIsConfirmationDialogOpen(true);
  };

  const confirmCopy = () => {
    setConfirmationContent({
      title: t("copyInvoice"),
      contentText: t("confirmCopyInvoiceInfo"),
      cancelText: t("common:cancel"),
      confirmText: t("copyInvoice"),
      confirmAction: copy,
    });
    setIsConfirmationDialogOpen(true);
  };

  const navigateToEditPage = () => {
    // If credit invoice bundle, navigate to edit bundle (ids in url)
    if (isSelectedCreditInvoiceBundleItem) {
      const creditInvoiceBundleInvoiceNumbers =
        selectedCreditInvoiceBundleItems.map(
          (creditInvoice) => creditInvoice.id!!,
        );
      navigate(
        `/home/invoices/edit/${creditInvoiceBundleInvoiceNumbers.join(",")}`,
      );
    } else {
      // Normal case
      navigate(`/home/invoices/edit/${selectedInvoice.id}`);
    }
  };

  // Keep state up to date for showing correct buttons and actions
  useEffect(() => {
    if (isSelectedCreditInvoiceBundleItem) {
      // If we have only one item in the bundle
      // we can use the status of that item to determine the button status and action
      if (selectedCreditInvoiceBundleItems.length === 1) {
        setAreCreditInvoiceBundleStatusesValidForSameAction(false);
      } else {
        /*
        Check if the statuses of the credit invoice bundle items are valid for the same action.
        If they are, we can use the status of the first invoice to determine the one button status and action that affects both invoices.
        If they are not, we cannot have one button that affects both invoices.
        -> We will fall back to individual buttons for each credit invoice (normal case)
        */

        // Check if the statuses of the credit invoice bundle items are valid for the same action.
        const notValidStatuses = selectedCreditInvoiceBundleItems.every(
          (item) =>
            item.invoiceStatus !==
              selectedCreditInvoiceBundleItems[0].invoiceStatus &&
            item.invoiceStatus === INVOICE_SENT_SUCCESS,
        );

        if (notValidStatuses) {
          setAreCreditInvoiceBundleStatusesValidForSameAction(false);
        }
      }
    }
    if (
      !!selectedInvoice.invoiceWave &&
      selectedInvoice.invoiceStatus !== INVOICE_SENT_SUCCESS
    ) {
      if (selectedInvoice.invoiceWave === INVOICE_WAVE_ONE) {
        setCanMarkFirstWave(false);
        setCanMarkSecondWave(true);
      } else if (selectedInvoice.invoiceWave === INVOICE_WAVE_TWO) {
        setCanMarkFirstWave(true);
        setCanMarkSecondWave(false);
      }
    } else {
      setCanMarkFirstWave(false);
      setCanMarkSecondWave(false);
    }
    switch (selectedInvoice.invoiceStatus) {
      case INVOICE_DRAFT:
      case INVOICE_EDITED:
      case INVOICE_READY:
      case INVOICE_SENT_FAILED:
        setCanSend(true);
        setCanResend(false);
        //setCanEdit(true);
        break;
      case INVOICE_SENT_SUCCESS:
        setCanSend(false);
        setCanResend(true);
        //setCanEdit(false);
        break;
      default:
        setCanSend(false);
        setCanResend(false);
        //setCanEdit(true);
        break;
    }
  }, [
    selectedInvoice,
    isSelectedCreditInvoiceBundleItem,
    selectedCreditInvoiceBundleItems,
    creditInvoiceBundleInvoices,
  ]);

  useEffect(() => {
    if (!user) return;
    setIsUserAllowedToEdit(canExpertModifyInvoices(user));
  }, [user]);

  const invoiceRowsSum = selectedInvoice.invoiceRows.reduce(
    (acc: number, row: InvoiceRow) => {
      if (!row.editable) {
        return acc + +row.amount;
      }
      return acc;
    },
    0,
  );

  const isNegativeInvoice = invoiceRowsSum < 0;
  const isCreditInvoice =
    selectedInvoice.originalInvoiceNumber && selectedInvoice.bundleId;

  const allowCreateNewCreditInvoice =
    selectedInvoice.invoiceStatus === INVOICE_SENT_SUCCESS &&
    !isInvoiceCreditInvoice(selectedInvoice);
  const allowConvertToCreditInvoice = isNegativeInvoice && !isCreditInvoice;

  return (
    <ActionBanner isOpen={true}>
      {isUserAllowedToEdit ? (
        <>
          <Box>
            <ActionButton
              variant="outlined"
              disabled={!canEdit}
              onClick={navigateToEditPage}
            >
              {t("edit", { ns: "common" })}
            </ActionButton>
            {!isSelectedCreditInvoiceBundleItem && (
              <ActionButton variant="outlined" onClick={confirmCopy}>
                {t("makeCopy", { ns: "common" })}
              </ActionButton>
            )}
            {canExamine && (
              <ActionToggleButton
                value="row-selection"
                selected={isRowSelectionEnabled}
                onChange={toggleRowSelectionEnabled}
                sx={{ margin: "0px 10px" }}
              >
                <PageviewIcon sx={{ marginRight: 1 }} />
                {t("examine")}
              </ActionToggleButton>
            )}
          </Box>
          {/* The whole next section is about how we show Send button */}
          <Box sx={{ marginLeft: "auto" }}>
            {allowCreateNewCreditInvoice && (
              <ActionButton
                variant="contained"
                color="primary"
                onClick={toggleCreateNewCreditInvoiceDialog}
              >
                {t("createCreditInvoice")}
              </ActionButton>
            )}
            {allowConvertToCreditInvoice && (
              <ActionButton
                variant="contained"
                color="error"
                onClick={toggleConvertToCreditInvoiceDialog}
              >
                {t("changeToCreditInvoice")}
              </ActionButton>
            )}
            {isValidCreditInvoiceBundle ? (
              // Credit invoice bundle special case if the invoice statuses are valid for same action
              <>
                {canSend ? (
                  <ActionButton variant="contained" onClick={confirmSending}>
                    {t("acknowledgeAndSendInvoices")}
                  </ActionButton>
                ) : canResend ? (
                  <ActionButton variant="contained" onClick={confirmSending}>
                    {t("resendInvoices")}
                  </ActionButton>
                ) : null}
              </>
            ) : canMarkFirstWave || canMarkSecondWave ? (
              // Invoice wave special cases
              <ActionButtonGroup>
                {!!canMarkFirstWave ? (
                  <GroupButton onClick={() => markWave(INVOICE_WAVE_ONE)}>
                    {t("markFirstWave")}
                  </GroupButton>
                ) : !!canMarkSecondWave ? (
                  <GroupButton onClick={() => markWave(INVOICE_WAVE_TWO)}>
                    {t("markSecondWave")}
                  </GroupButton>
                ) : null}
                {canSend ? (
                  <GroupButton onClick={confirmSending}>
                    {t("send")}
                  </GroupButton>
                ) : canResend ? (
                  <ActionButton variant="outlined" onClick={confirmSending}>
                    {t("resend")}
                  </ActionButton>
                ) : (
                  <GroupButton disabled>{t("send")}</GroupButton>
                )}
              </ActionButtonGroup>
            ) : canSend ? (
              // Normal case
              <ActionButton variant="outlined" onClick={confirmSending}>
                {t("send")}
              </ActionButton>
            ) : canResend ? (
              <ActionButton
                variant="outlined"
                hidden={!canResend}
                onClick={confirmSending}
              >
                {t("resend")}
              </ActionButton>
            ) : null}
          </Box>
        </>
      ) : (
        <Box>
          <ActionButton
            variant="outlined"
            disabled={!canEdit}
            onClick={navigateToEditPage}
          >
            {t("edit", { ns: "common" })}
          </ActionButton>
          {canExamine && (
            <ActionToggleButton
              value="row-selection"
              selected={isRowSelectionEnabled}
              onChange={toggleRowSelectionEnabled}
              sx={{ margin: "0px 10px" }}
            >
              <PageviewIcon sx={{ marginRight: 1 }} />
              {t("examine")}
            </ActionToggleButton>
          )}
        </Box>
      )}
      <InvoiceActionBannerPagingControl
        currentInvoice={currentInvoice}
        numberOfInvoices={numberOfInvoices}
        cycleInvoices={cycleInvoices}
      />
      <ConfirmationDialog
        title={confirmationContent.title}
        contentText={confirmationContent.contentText}
        cancelText={confirmationContent.cancelText}
        confirmText={confirmationContent.confirmText}
        isOpen={isConfirmationDialogOpen}
        onConfirm={confirmationContent.confirmAction}
        onCancel={() => setIsConfirmationDialogOpen(false)}
      />
      <OverlaySpinner isLoading={isLoading} />
    </ActionBanner>
  );
};
